import { uidMembershipLiterals } from '@core/filters/path-filter/uid-membership';
import { FilterField } from '@core/filters/path-filter/vocabulary/vocabulary';
import { FilterExpr } from '@core/filters/path-filter/wire';
import { FilterConnector } from '@core/models/entity/entity-selector-request.model';

import {
  FilterCondition,
  FilterConfiguration,
  FilterGroup
} from '@shared/components/filter-query-builder/filter.models';
import {
  pruneUnfilledConditions,
  uidListCondition
} from '@shared/utils/filter-condition.utils';

/**
 * The picker's request filter: the uids the configuration offers, AND whatever the operator
 * built in the filter bar.
 *
 * The restriction is **one** `UID_LIST` membership condition, which serialises to the
 * `uid_list_condition` the GBE filtering guide documents for matching several uids.
 *
 * A filter bar group that is *not* a plain AND keeps its own group — flattening would turn
 * its OR into an AND and drop rows the operator asked to see, which is worse than leaving it
 * in the shape it was built in.
 *
 * Conditions the operator has not filled in are pruned first, and that is load-bearing: the
 * data endpoint answers a tree containing one operator-without-a-value by discarding the
 * whole tree, so an unfilled condition does not merely fail to narrow — it takes the uid
 * restriction down with it and offers every entity in the plan.
 *
 * Returns `undefined` for an empty candidate list: a restriction that excludes everything is
 * not expressible here, so the caller must not fetch at all rather than fetch unrestricted.
 */
export function buildPickerFilter(
  entityType: string,
  uids: readonly string[],
  userFilter: FilterConfiguration | null
): FilterConfiguration | undefined {
  if (uids.length === 0) return undefined;

  const candidates = uidListCondition(
    'deploy-picker-candidate-uids',
    'uid',
    uids,
    entityType
  );

  return {
    rootGroup: {
      id: 'deploy-picker-filter',
      connector: FilterConnector.AND,
      conditions: [
        candidates,
        ...userConditions(pruneUnfilledConditions(userFilter)?.rootGroup)
      ]
    }
  };
}

/**
 * The Path counterpart of `buildPickerFilter`: the candidates as `uid is_any_of`, ANDed with what
 * the operator composed. A Path request carries no classic `filters`, so the restriction must
 * live in this tree.
 *
 * Returns `undefined` for an empty candidate list, as `buildPickerFilter` does: `is_any_of []` is
 * an incomplete condition the data layer refuses, and leaving the restriction out would widen the
 * picker to every entity in scope.
 */
export function pathPickerFilter(
  uidField: FilterField,
  uids: readonly string[],
  userTree: FilterExpr | undefined
): FilterExpr | undefined {
  if (uids.length === 0) return undefined;
  const restriction: FilterExpr = {
    predicate: {
      path: uidField.path,
      op: { is_any_of: uidMembershipLiterals(uidField, uids) }
    }
  };
  return userTree ? { and: [restriction, userTree] } : restriction;
}

/**
 * The response rows that really are candidates.
 *
 * `buildPickerFilter` already asks for the candidates only, so this normally drops nothing.
 * Asking is not enough: the data endpoint answers a legacy `filters` tree it cannot translate
 * by discarding the whole tree — the uid restriction with it — and returning every entity in
 * scope. A filter bar condition on a field the endpoint does not support therefore *widens*
 * the picker instead of narrowing it, and the operator can tick and deploy entities the
 * configuration never offered. ROUTE's `company_uid` is such a field today: the schema
 * annotates it `x-filterable` and the endpoint does not know it.
 *
 * A Path tree (`pathPickerFilter`) does not widen that way: the data layer resolves it against
 * the served filter schema and answers an unknown path or operator with an error, which the
 * picker shows as a load error. The rows are filtered on that path all the same, so the picker
 * stays restricted should the endpoint ever start dropping what it cannot resolve.
 */
export function keepCandidateRows<T extends { uid: string }>(
  rows: readonly T[],
  uids: readonly string[]
): T[] {
  const candidates = new Set(uids);
  return rows.filter((row) => candidates.has(row.uid));
}

function userConditions(group: FilterGroup | undefined): (FilterCondition | FilterGroup)[] {
  if (!group || group.conditions.length === 0) return [];
  if (group.connector === FilterConnector.AND || group.conditions.length === 1) {
    return group.conditions;
  }
  return [group];
}
